import { useEffect, useRef, useState } from "react";

import { useIdleWave } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/hooks/useIdleWave";
import { useWaveformAnalyser } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/hooks/useWaveformAnalyser";
import {
  INITIAL_SHOWCASE_ARTIST,
  INITIAL_SHOWCASE_SONG,
} from "@/constants/conversionShowcase";
import { useAudioPlaying } from "@/lib/zustand/useAudioPlaying";
import { ShowcaseArtist, ShowcaseSong } from "@/types/defs/conversionShowcase";

// Orchestrates the marketing conversion demo: two hidden <audio> elements (raw input audio vs converted output audio), selection state, and coordination with the global audio store so other players on the site pause when this demo plays.
export const useConversionShowcase = () => {
  const [selectedSong, setSelectedSong] = useState<ShowcaseSong>(
    INITIAL_SHOWCASE_SONG
  );
  const [selectedVoice, setSelectedVoice] = useState<ShowcaseArtist>(
    INITIAL_SHOWCASE_ARTIST
  );
  const [isOutputPlaying, setIsOutputPlaying] = useState(false);
  const [playingInputLabel, setPlayingInputLabel] = useState<string | null>(
    null
  );

  // Converted output audio (wired through Web Audio in useWaveformAnalyser).
  const outputAudioRef = useRef<HTMLAudioElement | null>(null);
  // Separate element for raw input audio so users can preview songs without touching the output pipeline.
  const inputAudioRef = useRef<HTMLAudioElement | null>(null);

  const audioPlaying = useAudioPlaying((state) => state.audioPlaying);
  const setAudioPlaying = useAudioPlaying((state) => state.setAudioPlaying);
  const pauseAudio = useAudioPlaying((state) => state.pauseAudio);
  const setAudioPlayingToZero = useAudioPlaying(
    (state) => state.setAudioPlayingToZero
  );

  // When not playing the output, show the idle animation instead of stale FFT data.
  const idleWave = useIdleWave(!isOutputPlaying);
  const { waveformData, ensureAudioGraph } = useWaveformAnalyser(
    outputAudioRef,
    isOutputPlaying
  );

  // Register ended listeners once on mount
  useEffect(() => {
    const outputAudio = outputAudioRef.current;
    const inputAudio = inputAudioRef.current;

    const handleOutputEnded = () => setIsOutputPlaying(false);
    const handleInputEnded = () => setPlayingInputLabel(null);

    outputAudio?.addEventListener("ended", handleOutputEnded);
    inputAudio?.addEventListener("ended", handleInputEnded);

    return () => {
      outputAudio?.removeEventListener("ended", handleOutputEnded);
      inputAudio?.removeEventListener("ended", handleInputEnded);
    };
  }, []);

  // Another player took over — reset our button states.
  useEffect(() => {
    if (audioPlaying !== outputAudioRef.current) {
      setIsOutputPlaying(false);
    }
    if (audioPlaying !== inputAudioRef.current) {
      setPlayingInputLabel(null);
    }
  }, [audioPlaying]);

  useEffect(() => {
    // New voice model or input song means different audio URLs; stop both elements.
    const outputAudio = outputAudioRef.current;
    const inputAudio = inputAudioRef.current;
    if (outputAudio !== null) {
      outputAudio.pause();
      outputAudio.currentTime = 0;
    }
    if (inputAudio !== null) {
      inputAudio.pause();
      inputAudio.currentTime = 0;
    }
    setIsOutputPlaying(false);
    setPlayingInputLabel(null);
  }, [selectedVoice, selectedSong]);

  const stopAudioElement = (
    audio: HTMLAudioElement | null,
    onStop: () => void
  ) => {
    if (audio === null) {
      return;
    }
    audio.pause();
    audio.currentTime = 0;
    onStop();
  };

  // Stops global audio, sets src, and plays. Returns true if playback started successfully.
  const playAudio = async (
    audio: HTMLAudioElement,
    src: string,
    beforePlay?: () => Promise<void>
  ): Promise<boolean> => {
    if (audioPlaying !== null) {
      pauseAudio();
      setAudioPlayingToZero();
    }
    if (beforePlay !== undefined) {
      await beforePlay();
    }
    audio.src = src;
    try {
      await audio.play();
      setAudioPlaying(audio);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  // Play or stop the converted output audio for the current input song + voice model.
  const handlePlayOutput = async () => {
    const outputAudio = outputAudioRef.current;
    if (outputAudio === null) {
      return;
    }

    stopAudioElement(inputAudioRef.current, () => setPlayingInputLabel(null));

    if (isOutputPlaying) {
      outputAudio.pause();
      setIsOutputPlaying(false);
      return;
    }

    const played = await playAudio(
      outputAudio,
      selectedSong.conversions[selectedVoice.id],
      ensureAudioGraph
    );
    if (played) {
      setIsOutputPlaying(true);
    }
  };

  // Play or stop the raw input audio for a song row (mutually exclusive with output playback).
  const handlePlayInput = async (song: ShowcaseSong) => {
    const inputAudio = inputAudioRef.current;
    if (inputAudio === null) {
      return;
    }

    stopAudioElement(outputAudioRef.current, () => setIsOutputPlaying(false));

    if (playingInputLabel === song.label) {
      stopAudioElement(inputAudio, () => setPlayingInputLabel(null));
      return;
    }

    const played = await playAudio(inputAudio, song.url);
    if (played) {
      setPlayingInputLabel(song.label);
    }
  };

  const activeWave = waveformData.length > 0;
  const displayWave = activeWave ? waveformData : idleWave;
  // Drives connector SVG re-measure and any keyed transitions when selection changes.
  const animKey = `${selectedSong.label}-${selectedVoice.id}`;

  return {
    selectedSong,
    setSelectedSong,
    selectedVoice,
    setSelectedVoice,
    isOutputPlaying,
    playingInputLabel,
    outputAudioRef,
    inputAudioRef,
    handlePlayOutput,
    handlePlayInput,
    displayWave,
    activeWave,
    animKey,
  };
};
